import React, { useEffect } from 'react';
import T from '../../components/common/T';
import { useAccounting } from '../../context/AccountingContext';
import { 
    BanknotesIcon, 
    ArrowTrendingUpIcon, 
    ArrowTrendingDownIcon, 
    ClockIcon,
    DocumentTextIcon,
    ClipboardDocumentCheckIcon,
    ArrowTopRightOnSquareIcon
} from "@heroicons/react/24/outline";

// 🟢 IMPORT GLOBAL CURRENCY HOOK
import { useCurrency } from '../../hooks/useCurrency';

export default function AccountingDashboard() {
    const { stats, fetchStats, loading } = useAccounting();
    
    // 🟢 PULL GLOBAL FORMATTER
    const { formatMoney } = useCurrency();

    useEffect(() => {
        fetchStats();
    }, [fetchStats]);

    const StatCard = ({ title, value, icon: Icon, color, subtext, onClick }) => {
        const circleStyles = {
            green: 'bg-emerald-50 text-emerald-600 dark:bg-emerald-950/40 dark:text-emerald-400',
            red: 'bg-rose-50 text-rose-600 dark:bg-rose-950/40 dark:text-rose-400',
            blue: 'bg-sky-50 text-sky-600 dark:bg-sky-950/40 dark:text-sky-400',
        }[color] || 'bg-orange-50 text-[#E8612C] dark:bg-orange-950/40 dark:text-[#E8612C]';

        return (
            <div 
                onClick={onClick}
                className="bg-white dark:bg-[#161b22] border border-gray-100 dark:border-[#30363d]/80 rounded-2xl p-4 sm:p-5 shadow-xs hover:shadow-md transition-all duration-200 flex flex-col justify-between group hover:-translate-y-0.5 cursor-pointer"
            >
                {/* Top Section: Icon on left, Subtext Badge on right */}
                <div className="flex items-center justify-between mb-3.5">
                    <div className={`w-10 h-10 sm:w-11 sm:h-11 rounded-xl flex items-center justify-center shrink-0 ${circleStyles} transition-transform duration-200 group-hover:scale-105`}>
                        <Icon className="w-5 h-5" strokeWidth={1.8} />
                    </div>
                    {subtext && (
                        <span className="text-[10px] font-semibold text-gray-500 dark:text-slate-400 bg-gray-100/80 dark:bg-[#21262d] px-2 py-0.5 rounded-md shrink-0">
                            <T>{subtext}</T>
                        </span>
                    )}
                </div>

                {/* Middle Section: Uncompressed Full-Width Value & Title */}
                <div className="min-w-0">
                    <h3 className="text-2xl sm:text-[26px] font-black tracking-tight text-gray-900 dark:text-white tabular-nums leading-none">
                        {loading && value === undefined ? "..." : formatMoney(value)}
                    </h3>
                    <p className="text-xs sm:text-[13px] font-medium text-gray-500 dark:text-slate-400 mt-1.5 capitalize leading-tight">
                        <T>{title}</T>
                    </p>
                </div>

                {/* Bottom Section */}
                <div className="border-t border-gray-100 dark:border-[#21262d] mt-3.5 pt-2.5 flex items-center justify-between">
                    <span className="text-[11px] sm:text-xs font-semibold text-gray-400 dark:text-slate-400 group-hover:text-[#E8612C] transition-colors">
                        <T>View Details</T>
                    </span>
                    <ArrowTopRightOnSquareIcon className="w-3.5 h-3.5 text-gray-400 dark:text-slate-500 group-hover:text-[#E8612C] group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-all duration-150" />
                </div>
            </div>
        );
    };

    return (
        <div className="p-4 sm:p-8 bg-gray-50 dark:bg-[#0d1117] min-h-screen transition-colors duration-200">
            {/* Header */}
            <div className="mb-8">
                <h1 className="text-base font-bold text-gray-900 dark:text-white"><T>Financial Overview</T></h1>
                <p className="text-gray-500 dark:text-slate-400 text-sm"><T>Real-time snapshot of your company's financial health.</T></p>
            </div>

            {/* Key Metrics Grid */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-10">
                <StatCard 
                    title="Total Income" 
                    value={stats.totalIncome} 
                    icon={ArrowTrendingUpIcon} 
                    color="green" 
                    subtext="Verified Invoices"
                />
                <StatCard 
                    title="Total Expense" 
                    value={stats.totalExpense} 
                    icon={ArrowTrendingDownIcon} 
                    color="red" 
                    subtext="Vendor Bills Recorded"
                />
                <StatCard 
                    title="Accounts Receivable" 
                    value={stats.pendingReceivables} 
                    icon={ClockIcon} 
                    color="blue" 
                    subtext="Pending Payments (In)"
                />
                <StatCard 
                    title="Accounts Payable" 
                    value={stats.pendingPayables} 
                    icon={BanknotesIcon} 
                    color="red" 
                    subtext="Pending Bills (Out)"
                />
            </div>

            {/* Quick Actions */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                {/* Recent Activity / Actions */}
                <div className="bg-white dark:bg-[#161b22] rounded-xl border border-gray-200 dark:border-[#30363d] shadow-sm p-6">
                    <h3 className="font-bold text-gray-800 dark:text-white mb-4 text-lg"><T>Quick Actions</T></h3>
                    <div className="grid grid-cols-2 gap-4">
                        <button className="flex items-center justify-center gap-2 p-4 border border-dashed border-gray-300 dark:border-[#30363d] rounded-lg hover:bg-gray-50 dark:hover:bg-slate-800 hover:border-blue-500 dark:hover:border-blue-400 transition group">
                            <DocumentTextIcon className="w-6 h-6 text-gray-400 dark:text-slate-400 group-hover:text-blue-600 dark:group-hover:text-blue-400" />
                            <span className="text-sm font-medium text-gray-600 dark:text-slate-300 group-hover:text-blue-700 dark:group-hover:text-blue-400"><T>New Customer Invoice</T></span>
                        </button>
                        <button className="flex items-center justify-center gap-2 p-4 border border-dashed border-gray-300 dark:border-[#30363d] rounded-lg hover:bg-gray-50 dark:hover:bg-slate-800 hover:border-red-500 dark:hover:border-rose-400 transition group">
                            <ClipboardDocumentCheckIcon className="w-6 h-6 text-gray-400 dark:text-slate-400 group-hover:text-red-600 dark:group-hover:text-rose-400" />
                            <span className="text-sm font-medium text-gray-600 dark:text-slate-300 group-hover:text-red-700 dark:group-hover:text-rose-400"><T>Record Vendor Bill</T></span>
                        </button>
                    </div>
                </div>

                {/* Profit Visualization Placeholder */}
                <div className="bg-white dark:bg-[#161b22] rounded-xl border border-gray-200 dark:border-[#30363d] shadow-sm p-6 flex flex-col justify-center items-center text-center">
                    <div className="w-16 h-16 bg-gray-100 dark:bg-slate-800 rounded-full flex items-center justify-center mb-3">
                        <ArrowTrendingUpIcon className="w-8 h-8 text-gray-400 dark:text-slate-400" />
                    </div>
                    <h3 className="font-bold text-gray-800 dark:text-white text-lg"><T>Net Profit Trend</T></h3>
                    <p className="text-sm text-gray-500 dark:text-slate-400 mt-1"><T>Charts & Analytics module coming in next phase.</T></p>
                    <div className="mt-4 text-2xl font-bold text-green-600 dark:text-emerald-400">
                        {/* 🟢 REPLACED LOCAL FORMATTER HERE */}
                        {formatMoney(stats.totalIncome - stats.totalExpense)}
                    </div>
                    <span className="text-xs text-gray-400 dark:text-slate-500 uppercase font-bold"><T>Net Profit (Est.)</T></span>
                </div>
            </div>
        </div>
    );
}